import { Controller } from '@hotwired/stimulus';

import { isAuthenticated, onUserStateChange } from '../../features/authentication/authentication';
import { locationAssign } from '../../Shared/path-helpers';

export const TIME_UNTIL_POLLING_MILLISECONDS = 600_000; // wait 10 minutes
export const POLL_INTERVAL_MILLISECONDS = 60_000; // then check every minute to see if the session expired

class SessionManager {
  #isEnabled: boolean = false;

  #waitingTimer?: number;

  #pollingInterval?: number;

  enable(): void {
    if (!this.#isEnabled) {
      this.#waitingTimer = window.setTimeout(() => this.#startPolling(), TIME_UNTIL_POLLING_MILLISECONDS);
      this.#isEnabled = true;
    }
  }

  disable(): void {
    clearTimeout(this.#waitingTimer);
    clearInterval(this.#pollingInterval);

    this.#waitingTimer = undefined;
    this.#pollingInterval = undefined;
    this.#isEnabled = false;
  }

  #startPolling(): void {
    this.#pollingInterval = window.setInterval(() => this.#validateSession(), POLL_INTERVAL_MILLISECONDS);
  }

  async #validateSession(): Promise<void> {
    const response = await fetch('/app/session');
    if (response.status === 401) {
      const query = window.location.search;
      const returnTo = `${window.location.pathname}${query}`;
      locationAssign(`/app/login?return_to=${encodeURIComponent(returnTo)}`);
    }
  }
}

export const sessionManager = new SessionManager();

export default class extends Controller {
  #unsubscribe?: () => void;

  connect(): void {
    this.#syncSessionManager();
    this.#unsubscribe = onUserStateChange(this.#syncSessionManager);
  }

  disconnect(): void {
    this.#unsubscribe?.();
    this.#unsubscribe = undefined;
  }

  #syncSessionManager = (): void => {
    if (isAuthenticated()) {
      sessionManager.enable();
    } else {
      sessionManager.disable();
    }
  };
}
